// Static clone resources follow the canonical application language.
import canonicalI18n, { createInstance } from 'i18next';

import frCommon from './locales/fr/common.json';
import frNav from './locales/fr/nav.json';
import frBottomNav from './locales/fr/bottomNav.json';
import frModeSwitcher from './locales/fr/modeSwitcher.json';
import frAdminCommon from './locales/fr/admin_common.json';

import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enBottomNav from './locales/en/bottomNav.json';
import enModeSwitcher from './locales/en/modeSwitcher.json';
import enAdminCommon from './locales/en/admin_common.json';

import esCommon from './locales/es/common.json';
import esNav from './locales/es/nav.json';
import arCommon from './locales/ar/common.json';
import arNav from './locales/ar/nav.json';
import itCommon from './locales/it/common.json';
import itNav from './locales/it/nav.json';

export const SOVERDI_LANGUAGE_STORAGE_KEY = 'agrisafe_client_language';

export const SOVERDI_SUPPORTED_LANGUAGES = ['fr', 'en', 'es', 'ar', 'it'] as const;

export type SoverdiLanguage = (typeof SOVERDI_SUPPORTED_LANGUAGES)[number];

export const readStoredSoverdiLanguage = (): SoverdiLanguage => {
  if (typeof window === 'undefined') return 'fr';
  try {
    const stored = window.localStorage.getItem(SOVERDI_LANGUAGE_STORAGE_KEY);
    if (stored && SOVERDI_SUPPORTED_LANGUAGES.includes(stored as SoverdiLanguage)) {
      return stored as SoverdiLanguage;
    }
  } catch {
    // Browser storage can be unavailable; French remains the safe default.
  }
  return 'fr';
};

export const persistSoverdiLanguage = (language: SoverdiLanguage): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SOVERDI_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // The current-session language still applies even if storage is unavailable.
  }
};

const RTL_DIRECTION = 'rtl';
const LTR_DIRECTION = 'ltr';
const DEFAULT_LANGUAGE = 'fr';

export const applySoverdiDocumentLanguage = (language: SoverdiLanguage): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
  document.documentElement.dir = language === 'ar' ? RTL_DIRECTION : LTR_DIRECTION;
};

const initialLanguage = canonicalI18n.resolvedLanguage ?? canonicalI18n.language ?? DEFAULT_LANGUAGE;

const soverdiCloneI18n = createInstance();

soverdiCloneI18n.init({
  lng: initialLanguage,
  fallbackLng: 'fr',
  supportedLngs: [...SOVERDI_SUPPORTED_LANGUAGES],
  ns: ['common', 'nav', 'bottomNav', 'modeSwitcher', 'admin_common'],
  defaultNS: 'common',
  fallbackNS: 'common',
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  resources: {
    fr: {
      common: frCommon,
      nav: frNav,
      bottomNav: frBottomNav,
      modeSwitcher: frModeSwitcher,
      admin_common: frAdminCommon,
    },
    en: {
      common: enCommon,
      nav: enNav,
      bottomNav: enBottomNav,
      modeSwitcher: enModeSwitcher,
      admin_common: enAdminCommon,
    },
    es: { common: esCommon, nav: esNav },
    ar: { common: arCommon, nav: arNav },
    it: { common: itCommon, nav: itNav },
  },
});

// Keep the canonical provider as React's default; only mirror its language here.
canonicalI18n.on('languageChanged', (language) => {
  void soverdiCloneI18n.changeLanguage(language);
});

export default soverdiCloneI18n;
